import React, { useEffect, useState } from 'react';
import { AutoComplete, Image, Input } from 'antd';
import { useAppDispatch } from '../../../hooks/redux';
import { createTagAction, searchTagsCachedAction } from '../../../redux/actions/tagActions';
import { Tag as TagType } from '../../../redux/actions/tagActions/types';
import { alertService } from '../../../services';
import {
    NewTag,
    ProductTagsModalStyled,
    ProductTagsSpin,
    SearchInput,
    SeeAllTagsButton,
    ModalContent,
    TitleContainer,
    SearchRow,
    SelectedTagsContainer,
    StyledSpace,
    Section,
    ColorPickerWrap,
} from '../../favoriteProductTagModal/FavoriteProductTagsModal.styles';
import ModalTitle from '../modalTitle/ModalTitle';
import { CloseIcon, SubTitle, Tag } from '../../../styles/common.styles';
import ModalFooter from '../modalFooter/ModalFooter';
import ColorPicker from '../colorPicker/ColorPicker';
import { getColorsAction } from '../../../redux/actions/colorsActions';
import { useDefaultColor } from '../../../hooks/useDefaultColor';
import { TagsDivider } from '../../tags/TagsPicker/TagsPicker.styles';

export interface TagAssignmentModalProps {
    visible: boolean;
    /** Already-assigned tag IDs for the entity (used to filter duplicates) */
    existingTagIds: number[];
    onClose: () => void;
    onSaved?: () => void;
    /** Called once per tag to assign. Receives the tagId to add. */
    onAssign: (tagId: number) => Promise<unknown>;
    /** Called after a successful save to refresh tags in the store. */
    onRefresh: () => void;
    inputId?: string;
}

const TagAssignmentModal: React.FC<TagAssignmentModalProps> = ({
    visible,
    existingTagIds,
    onClose,
    onSaved,
    onAssign,
    onRefresh,
    inputId = 'add-tag-input',
}) => {
    const dispatch = useAppDispatch();
    const [search, setSearch] = useState('');
    const [searchResults, setSearchResults] = useState<TagType[]>([]);
    const [selectedTags, setSelectedTags] = useState<TagType[]>([]);
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(false);
    const [newTagName, setNewTagName] = useState('');
    const [newTagColor, setNewTagColor] = useDefaultColor(visible);
    const [popoverVisible, setPopoverVisible] = useState(false);

    useEffect(() => {
        dispatch(getColorsAction());
    }, []);

    useEffect(() => {
        if (!visible) {
            setSearch('');
            setSearchResults([]);
            setSelectedTags([]);
            setNewTagName('');
            setNewTagColor(null);
        } else {
            dispatch(searchTagsCachedAction({ curPage: 1, pageSize: 20, searchString: '' }))
                .then((res: any) => {
                    const items = res.payload?.data?.items ?? res.payload?.data ?? [];
                    setSearchResults(items);
                })
                .catch(() => alertService.error('Tag search failed'));
        }
    }, [visible]);

    const searchDebounceRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const handleSearch = (val: string) => {
        setSearch(val);
        globalThis.clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = globalThis.setTimeout(() => {
            setSearching(true);
            dispatch(searchTagsCachedAction({ curPage: 1, pageSize: 20, searchString: val }))
                .then((res: any) => {
                    const items = res.payload?.data?.items ?? res.payload?.data ?? [];
                    setSearchResults(items);
                })
                .catch(() => alertService.error('Tag search failed'))
                .finally(() => setSearching(false));
        }, 250);
    };

    const addTagToSelection = (tag: TagType) => {
        if (
            !selectedTags.some((t) => t.id === tag.id) &&
            !existingTagIds.includes(tag.id)
        ) {
            setSelectedTags((prev) => [...prev, tag]);
        }
    };

    const removeSelectedTag = (tagId: number) => {
        setSelectedTags((prev) => prev.filter((t) => t.id !== tagId));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            let tagsToCreate: TagType[] = [...selectedTags];

            if (newTagName?.trim() && newTagColor) {
                const createResult = await dispatch(
                    createTagAction({ name: newTagName.trim(), colorId: newTagColor?.id }),
                );
                // @ts-ignore
                if (createTagAction.fulfilled.match(createResult)) {
                    // @ts-ignore
                    const created = (createResult.payload?.data ?? createResult.payload) as TagType;
                    const newTag: TagType = {
                        id: created?.id,
                        organizationId: created?.organizationId,
                        name: created?.name,
                        color: created?.color,
                        isEditable: true,
                        colorId: created?.colorId,
                    };
                    setSearchResults((prev) => [newTag, ...prev]);
                    tagsToCreate = [...tagsToCreate, newTag];
                    setNewTagName('');
                    setNewTagColor(null);
                } else {
                    alertService.error('Failed to create tag');
                    setSaving(false);
                    return;
                }
            }

            const toAdd = tagsToCreate.filter((t) => !existingTagIds.includes(t.id));
            const results = await Promise.allSettled(
                toAdd.map((t) => onAssign(t.id))
            );

            const failed = results
                .map((r, i) => ({ r, id: toAdd[i].id }))
                .filter(x => x.r.status === 'rejected')
                .map(x => x.id);

            if (failed.length) {
                alertService.error(`Failed to update ${failed.length} product tag(s)`);
            } else {
                alertService.success(`Successfully updated ${toAdd.length} product tag(s)`);
            }
            onRefresh();
            if (onSaved) {
                onSaved();
            }
            onClose();
        } catch (e) {
            console.error(e);
            alertService.error('Failed to save tags');
        } finally {
            setSaving(false);
        }
    };

    const filteredSearchResults = searchResults
        .filter((r) => !existingTagIds.includes(r.id))
        .filter((r) => !selectedTags.some((st) => st.id === r.id))
        .filter((r) => (search ? r.name?.toLowerCase().includes(search.toLowerCase()) : true));

    const basicTags = filteredSearchResults.filter((t) => !t.organizationId);
    const orgTags = filteredSearchResults.filter((t) => t.organizationId).sort((a, b) => a.name.localeCompare(b.name));

    const tagOptions: any[] = [];
    if (basicTags.length > 0) {
        tagOptions.push(...basicTags.map((item) => ({
            value: item.id?.toString() ?? item.name,
            label: (
                <Tag color={item?.color?.display} borderColor={item?.color?.baseColor}>
                    {item?.name}
                </Tag>
            ),
        })));
    }
    if (orgTags.length > 0) {
        if (basicTags.length > 0) {
            tagOptions.push({
                value: 'divider',
                className: 'tag-picker-divider',
                label: <TagsDivider />,
                disabled: true,
            });
        }
        tagOptions.push(...orgTags.map((item) => ({
            value: item.id?.toString() ?? item.name,
            label: (
                <Tag color={item?.color?.display} borderColor={item?.color?.baseColor}>
                    {item?.name}
                </Tag>
            ),
        })));
    }
    return (
        <ProductTagsModalStyled
            open={visible}
            onCancel={onClose}
            centered
            footer={null}
            closeIcon={<CloseIcon />}
        >
            <ProductTagsSpin spinning={searching || saving}>
                <ModalContent>
                    <TitleContainer>
                        <ModalTitle title="Add tag" icon="tag.svg" />
                    </TitleContainer>
                    <SubTitle>Search current tags</SubTitle>
                    <SearchRow>
                        <AutoComplete
                            style={{ width: '280px' }}
                            popupClassName="tag-picker-dropdown"
                            getPopupContainer={(trigger) => trigger.parentElement || document.body}
                            options={tagOptions}
                            onSelect={(value) => {
                                if (value === 'divider') {
                                    return;
                                }
                                const id = Number.parseInt(value, 10);
                                const item = searchResults.find((s) => s.id === id);
                                if (item) {
                                    addTagToSelection(item);
                                }
                            }}
                            onSearch={(val) => handleSearch(val)}
                            value={search}
                        >
                            <SearchInput
                                placeholder="Search tag names"
                                prefix={
                                    <Image
                                        src="src/assets/images/magnifying-glass.svg"
                                        preview={false}
                                    />
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSearch(search);
                                    }
                                }}
                                onChange={(e) => handleSearch(e.target.value)}
                                value={search}
                            />
                        </AutoComplete>
                        <SeeAllTagsButton
                            type="link"
                            onClick={() => {
                                window.open(`${globalThis.location.origin}/tags`, '_blank');
                            }}
                        >
                            See all tags
                        </SeeAllTagsButton>
                    </SearchRow>
                    {selectedTags.length > 0 && (
                        <SelectedTagsContainer>
                            <StyledSpace wrap>
                                {selectedTags.map((t) => (
                                    <Tag
                                        key={t.id}
                                        closable
                                        onClose={() => removeSelectedTag(t.id)}
                                        color={t?.color?.display}
                                        borderColor={t?.color?.baseColor}
                                    >
                                        {t?.name}
                                    </Tag>
                                ))}
                            </StyledSpace>
                        </SelectedTagsContainer>
                    )}
                    <Section>
                        <SubTitle>Add new tag</SubTitle>
                        <NewTag>
                            <Input
                                placeholder="Enter tag name"
                                onChange={(e) => setNewTagName(e.target.value)}
                                value={newTagName}
                                id={inputId}
                                maxLength={40}
                                style={{ width: '280px' }}
                            />
                            <ColorPickerWrap>
                                <ColorPicker
                                    color={newTagColor}
                                    setColor={setNewTagColor}
                                    popoverVisible={popoverVisible}
                                    setPopoverVisible={setPopoverVisible}
                                />
                            </ColorPickerWrap>
                        </NewTag>
                    </Section>
                    <ModalFooter
                        cancel={onClose}
                        submit={handleSave}
                        action="Save"
                        cancelText="Cancel"
                    />
                </ModalContent>
            </ProductTagsSpin>
        </ProductTagsModalStyled>
    );
};

export default TagAssignmentModal;
